import {
  CAPABILITIES,
  ROLE_DEFAULT_CAPABILITIES,
  USER_ROLES,
  type Capability,
  type UserRole,
} from '@admitly/shared';

/** Display labels for the capabilities (Brief 3c — wording can be tweaked later). */
export const CAP_LABELS: Record<Capability, string> = {
  view_pricing: 'See pricing',
  edit_pricing: 'Edit pricing',
  view_commission: 'See commission',
  edit_commission: 'Edit commission',
  manage_users: 'Manage users',
  view_all_branches: 'See all offices',
  manage_catalogue: 'Manage catalogue',
  create_quotes: 'Create quotes',
  send_documents: 'Send documents',
};

/** Display labels for the 6 roles. */
export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Master admin',
  admin: 'Admin',
  owner: 'Owner',
  consultant: 'Consultant',
  accounts: 'Accounts',
  offshore: 'Offshore',
};

/** The roles this screen can assign — every role except super_admin (no promotion to master admin). */
export const ASSIGNABLE_ROLES: UserRole[] = USER_ROLES.filter((r) => r !== 'super_admin');

export type OverrideRow = { capability: Capability; granted: boolean };
export type CapabilitySource = 'override-on' | 'override-off' | 'role';

export interface CapabilityRow {
  capability: Capability;
  label: string;
  /** Whether the switch is ON. */
  on: boolean;
  source: CapabilitySource;
  /** Tag shown above the switch. */
  tag: 'Turned on' | 'Turned off' | 'From role';
  /** True when a per-user override exists — the panel shows a "Reset" link. */
  overridden: boolean;
}

/**
 * Build the 8 capability rows for the panel. The API does NOT tag each switch, so
 * we derive it here from the role defaults ± the per-user overrides: an override
 * wins (and reads "Turned on"/"Turned off"); otherwise the switch reflects the role
 * default and reads "From role".
 */
export function computeRows(role: UserRole, overrides: readonly OverrideRow[]): CapabilityRow[] {
  const defaults = ROLE_DEFAULT_CAPABILITIES[role];
  return CAPABILITIES.map((capability) => {
    const ov = overrides.find((o) => o.capability === capability);
    if (ov) {
      return {
        capability,
        label: CAP_LABELS[capability],
        on: ov.granted,
        source: ov.granted ? 'override-on' : 'override-off',
        tag: ov.granted ? 'Turned on' : 'Turned off',
        overridden: true,
      };
    }
    return {
      capability,
      label: CAP_LABELS[capability],
      on: defaults.includes(capability),
      source: 'role',
      tag: 'From role',
      overridden: false,
    };
  });
}

export type FlipAction = { kind: 'clear' } | { kind: 'put'; granted: boolean };

/**
 * Decide what request a switch flip should make, keeping overrides MINIMAL: if the
 * new state equals the role default, DELETE the override (back to "From role");
 * otherwise PUT it. This guarantees an override row exists only when the person
 * differs from their role, so the tags stay truthful.
 */
export function flipAction(role: UserRole, capability: Capability, next: boolean): FlipAction {
  const isDefault = ROLE_DEFAULT_CAPABILITIES[role].includes(capability);
  return next === isDefault ? { kind: 'clear' } : { kind: 'put', granted: next };
}
